function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo } from 'react';
import cn from 'classnames';
import { relationshipModel } from "../../../models";
import { BillingGroupTypes, summarize } from "../genServicesSummary";
import { getBillingGroupTitle } from "./BillingSchedule.utils";
import { processBillingGroups } from "./BillingSchedule.utils";
import { utils } from "../../../utils";
import { Badge, Callout, Tooltip, Typography } from 'seagull';
import { useResponsive } from "../../contexts";
import { useIoContext } from "../../../contexts";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { useServicesBillingDetails } from "../../../hooks/reactQuery/queries/useServicesBillingDetails";
import styles from "./BillingSchedule.module.scss";
import BillingStageRow from "./BillingStageRow";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
function renderServiceRow(_ref) {
  var _servicesBillingDetai;
  let {
    variablePrice,
    name,
    price,
    minPrice,
    isRange,
    unit,
    prorated,
    hasBilledUpfront,
    groupType,
    serviceId,
    servicesBillingDetails,
    START_BILLING_AT
  } = _ref;
  const priorPeriods = ((_servicesBillingDetai = servicesBillingDetails[serviceId]) === null || _servicesBillingDetai === void 0 ? void 0 : _servicesBillingDetai.periods) || 0;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Typography, {
      children: variablePrice && !isRange ? "".concat(name, " (").concat(utils.formatDollarAmount(price), "/").concat(unit || 'hour', ")") : isRange ? "".concat(name, " (").concat(minPrice != null ? "".concat(utils.formatDollarAmount(minPrice), " - ") : 'up to ').concat(utils.formatDollarAmount(price), ")") : "".concat(name, " ").concat(priorPeriods > 0 && START_BILLING_AT ? "(".concat(priorPeriods, " prior periods)") : '')
    }), prorated && (groupType === BillingGroupTypes.Today || !hasBilledUpfront) && /*#__PURE__*/_jsx(Tooltip, {
      placement: "right",
      title: 'The final payment amount will be calculated based on the proposal approval date',
      children: /*#__PURE__*/_jsx(Badge, {
        rounded: true,
        outlined: true,
        skin: "warning",
        className: styles.billingScheduleListItemBadge,
        "data-testid": "billing-schedule-service-prorated-badge",
        children: "Prorated"
      })
    })]
  });
}
const BillingSchedule = _ref2 => {
  let {
    selectedOptionalServices,
    selectedBundleID,
    relationship
  } = _ref2;
  const {
    isMobile
  } = useResponsive();
  const {
    getNow
  } = useIoContext();
  const {
    START_BILLING_AT = false
  } = useFeatureFlags();
  const effectiveDate = relationshipModel.effectiveDate(relationship);
  const services = useMemo(() => relationshipModel.actualServices(relationship, selectedBundleID), [relationship, selectedBundleID]);
  const {
    servicesBillingDetails
  } = useServicesBillingDetails({
    services,
    relationshipEffectiveDate: effectiveDate
  });
  const billingSummary = useMemo(() => services.reduce(summarize({
    selectedOptionalServices: selectedOptionalServices || [],
    effectiveDate,
    now: getNow(),
    START_BILLING_AT
  }), {}), [services, selectedOptionalServices, effectiveDate, START_BILLING_AT]);
  const serviceNameToIdMap = useMemo(() => {
    const map = {};
    services.forEach(service => {
      map[service.name] = service.id;
    });
    return map;
  }, [services]);
  const getGroups = () => {
    return processBillingGroups(billingSummary, services, serviceNameToIdMap, servicesBillingDetails, START_BILLING_AT);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      className: cn(styles.sectionTitle, {
        [styles.mobile]: isMobile
      }),
      children: /*#__PURE__*/_jsx(Typography, {
        variant: "h6",
        weight: "bold",
        children: "Billing Schedule"
      })
    }), getGroups().map(_ref3 => {
      let {
        group,
        groupType,
        variablePrice,
        startDate
      } = _ref3;
      return /*#__PURE__*/_jsxs("div", {
        className: styles.billingSchedule,
        children: [/*#__PURE__*/_jsxs(Typography, {
          variant: "paragraph1",
          weight: "bold",
          showWhiteSpaces: true,
          breakWords: true,
          className: cn(styles.coverLetter, {
            [styles.mobile]: isMobile
          }),
          children: [getBillingGroupTitle(groupType, startDate, relationship), " -", ' ', !variablePrice ? utils.formatDollarAmount(group.totalAmount) : 'variable']
        }), groupType === BillingGroupTypes.Today && group.hasBilledUpfront && /*#__PURE__*/_jsx(Callout, {
          intent: "info",
          className: styles.billingScheduleListItem,
          children: "First invoice contains recurring services in an active billing period."
        }), group.services.map((service, index) => /*#__PURE__*/_jsx("ul", {
          className: cn(styles.listItem, styles.billingScheduleListItem),
          "data-testid": "billing-schedule-service",
          children: service.billingStages ? /*#__PURE__*/_jsx(BillingStageRow, {
            name: service.name,
            price: service.price,
            billingStages: service.billingStages
          }) : renderServiceRow(_objectSpread(_objectSpread({}, service), {}, {
            variablePrice,
            hasBilledUpfront: group.hasBilledUpfront,
            groupType,
            serviceId: serviceNameToIdMap[service.name] || '',
            servicesBillingDetails,
            START_BILLING_AT
          }))
        }, index))]
      }, "".concat(groupType, "-").concat(variablePrice, "-").concat(startDate === null || startDate === void 0 ? void 0 : startDate.toISOString()));
    })]
  });
};
export default BillingSchedule;